import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Snackbar,
  Autocomplete,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  QueueMusic,
  MusicNote,
  ContentCopy,
  Shuffle,
  Repeat,
  Close,
  PlaylistAdd,
} from '@mui/icons-material';
import {
  useSchedulingPlaylists,
  useSchedulingPlaylist,
  useCreateSchedulingPlaylist,
  useUpdateSchedulingPlaylist,
  useDeleteSchedulingPlaylist,
  useDuplicatePlaylist,
  useAddTrackToPlaylist,
  useRemoveTrackFromPlaylist,
} from '../../hooks/useScheduling';
import { useContentList } from '../../hooks/useContent';
import type { Playlist } from '../../api/types';
import { ContentWorkflowState } from '../../api/types';
import { SectionTabs } from '../../components/SectionTabs';
import { SCHEDULING_TABS, SCHEDULING_ACCENT_COLOR } from './schedulingTabs';
import { schedulingHelp } from '../../components/PageHelp/helpContent';
import { useOrgFilter } from '../../contexts/OrganizationContext';
import { DuplicateToOrgDialog } from '../../components/DuplicateToOrgDialog';

const COLOR_OPTIONS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16', '#22c55e', '#10b981',
  '#14b8a6', '#06b6d4', '#0ea5e9', '#3b82f6', '#6366f1', '#8b5cf6',
];

const PlaylistsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState<Playlist | null>(null);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicatingPlaylist, setDuplicatingPlaylist] = useState<Playlist | null>(null);

  // Use global organization filter
  const orgFilter = useOrgFilter();

  // Fetch playlists filtered by organization
  const { data: playlists, isLoading, error } = useSchedulingPlaylists({
    search: searchQuery || undefined,
    organization_id: orgFilter,
  });

  const createMutation = useCreateSchedulingPlaylist();
  const updateMutation = useUpdateSchedulingPlaylist();
  const deleteMutation = useDeleteSchedulingPlaylist();
  const duplicateMutation = useDuplicatePlaylist();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#3b82f6',
    shuffle_mode: true,
    repeat_mode: 'all' as 'none' | 'all' | 'one',
    crossfade_seconds: 3,
    explicit_filter: true,
    is_active: true,
  });

  const handleOpenCreate = () => {
    setFormData({
      name: '',
      description: '',
      color: '#3b82f6',
      shuffle_mode: true,
      repeat_mode: 'all',
      crossfade_seconds: 3,
      explicit_filter: true,
      is_active: true,
    });
    setEditingPlaylist(null);
    setCreateDialogOpen(true);
  };

  const handleOpenEdit = (playlist: Playlist) => {
    setFormData({
      name: playlist.name,
      description: playlist.description || '',
      color: playlist.color,
      shuffle_mode: playlist.shuffle_mode,
      repeat_mode: playlist.repeat_mode,
      crossfade_seconds: playlist.crossfade_seconds,
      explicit_filter: playlist.explicit_filter,
      is_active: playlist.is_active,
    });
    setEditingPlaylist(playlist);
    setCreateDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      if (editingPlaylist) {
        await updateMutation.mutateAsync({
          id: editingPlaylist.id,
          data: formData,
        });
      } else {
        // Backend will assign organization automatically
        await createMutation.mutateAsync(formData);
      }
      setCreateDialogOpen(false);
    } catch (err) {
      console.error('Failed to save playlist:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this playlist?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const handleOpenDuplicate = (playlist: Playlist) => {
    setDuplicatingPlaylist(playlist);
    setDuplicateDialogOpen(true);
  };

  const handleDuplicate = async (params: { name?: string; targetOrganizationId?: string }) => {
    if (!duplicatingPlaylist) return;
    await duplicateMutation.mutateAsync({
      playlistId: duplicatingPlaylist.id,
      name: params.name,
      targetOrganizationId: params.targetOrganizationId,
    });
  };

  const handleCardClick = (playlistId: string) => {
    setSelectedPlaylistId(playlistId);
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins} min`;
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load playlists: {(error as Error)?.message}
      </Alert>
    );
  }

  return (
    <Box>
      <SectionTabs
        title="Scheduling"
        tabs={SCHEDULING_TABS}
        accentColor={SCHEDULING_ACCENT_COLOR}
        help={schedulingHelp.playlists}
        actionButton={{
          label: 'New Playlist',
          icon: <Add />,
          onClick: handleOpenCreate,
        }}
      />

      {/* Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search playlists..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          size="small"
        />
      </Paper>

      {/* Playlists Grid */}
      <Grid container spacing={2}>
        {playlists?.map((playlist) => (
          <Grid item xs={12} sm={6} md={4} key={playlist.id}>
            <Card
              onClick={() => handleCardClick(playlist.id)}
              sx={{
                cursor: 'pointer',
                border: '2px solid',
                borderColor: playlist.color,
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 3,
                },
                opacity: playlist.is_active ? 1 : 0.6,
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <QueueMusic sx={{ color: playlist.color }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, flex: 1 }}>
                    {playlist.name}
                  </Typography>
                  <Tooltip title="Duplicate to Organization">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenDuplicate(playlist); }}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenEdit(playlist); }}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDelete(playlist.id); }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>

                {playlist.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {playlist.description}
                  </Typography>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  <Chip
                    size="small"
                    icon={<MusicNote />}
                    label={`${playlist.track_count} tracks`}
                    sx={{ bgcolor: `${playlist.color}20`, color: playlist.color }}
                  />
                  <Chip size="small" label={formatDuration(playlist.total_duration_seconds)} />
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {playlist.shuffle_mode && (
                    <Chip size="small" icon={<Shuffle />} label="Shuffle" variant="outlined" />
                  )}
                  {playlist.repeat_mode !== 'none' && (
                    <Chip size="small" icon={<Repeat />} label={playlist.repeat_mode} variant="outlined" />
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {playlists?.length === 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <QueueMusic sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No playlists yet
              </Typography>
              <Button variant="contained" onClick={handleOpenCreate} sx={{ mt: 2 }}>
                Create Playlist
              </Button>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Create/Edit Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPlaylist ? 'Edit Playlist' : 'Create New Playlist'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Color</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {COLOR_OPTIONS.map((color) => (
                  <Box
                    key={color}
                    onClick={() => setFormData({ ...formData, color })}
                    sx={{
                      width: 32, height: 32, borderRadius: 1, bgcolor: color, cursor: 'pointer',
                      border: formData.color === color ? '3px solid white' : 'none',
                      boxShadow: formData.color === color ? `0 0 0 2px ${color}` : 'none',
                    }}
                  />
                ))}
              </Box>
            </Box>
            <FormControl fullWidth>
              <InputLabel>Repeat Mode</InputLabel>
              <Select
                value={formData.repeat_mode}
                label="Repeat Mode"
                onChange={(e) => setFormData({ ...formData, repeat_mode: e.target.value as any })}
              >
                <MenuItem value="none">No Repeat</MenuItem>
                <MenuItem value="all">Repeat All</MenuItem>
                <MenuItem value="one">Repeat One</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Crossfade (seconds)"
              type="number"
              value={formData.crossfade_seconds}
              onChange={(e) => setFormData({ ...formData, crossfade_seconds: parseInt(e.target.value) || 0 })}
              inputProps={{ min: 0, max: 12 }}
            />
            <FormControlLabel
              control={<Switch checked={formData.shuffle_mode} onChange={(e) => setFormData({ ...formData, shuffle_mode: e.target.checked })} />}
              label="Shuffle mode"
            />
            <FormControlLabel
              control={<Switch checked={formData.explicit_filter} onChange={(e) => setFormData({ ...formData, explicit_filter: e.target.checked })} />}
              label="Filter explicit content"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={!formData.name || createMutation.isPending || updateMutation.isPending}>
            {editingPlaylist ? 'Save Changes' : 'Create Playlist'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Playlist Detail Dialog */}
      {selectedPlaylistId && (
        <PlaylistDetailDialog
          playlistId={selectedPlaylistId}
          onClose={() => setSelectedPlaylistId(null)}
        />
      )}

      {/* Duplicate to Organization Dialog */}
      {duplicatingPlaylist && (
        <DuplicateToOrgDialog
          open={duplicateDialogOpen}
          onClose={() => {
            setDuplicateDialogOpen(false);
            setDuplicatingPlaylist(null);
          }}
          entityType="playlist"
          entityId={duplicatingPlaylist.id}
          entityName={duplicatingPlaylist.name}
          currentOrganizationId={duplicatingPlaylist.organization_id}
          onDuplicate={handleDuplicate}
        />
      )}
    </Box>
  );
};

// Playlist Detail Dialog - for managing tracks
const PlaylistDetailDialog: React.FC<{
  playlistId: string;
  onClose: () => void;
}> = ({ playlistId, onClose }) => {
  const { data: playlist, isLoading } = useSchedulingPlaylist(playlistId);
  const { data: contentData } = useContentList({
    size: 100,
    filters: { workflow_state: [ContentWorkflowState.APPROVED] }
  });
  const addTrackMutation = useAddTrackToPlaylist();
  const removeTrackMutation = useRemoveTrackFromPlaylist();

  const [searchTrack, setSearchTrack] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const availableContent = contentData?.items || [];

  const handleAddTrack = async (contentId: string) => {
    try {
      await addTrackMutation.mutateAsync({ playlistId, contentId });
      setSnackbar({ open: true, message: 'Track added' });
      setSearchTrack('');
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to add track' });
    }
  };

  const handleRemoveTrack = async (itemId: string) => {
    try {
      await removeTrackMutation.mutateAsync({ playlistId, itemId });
      setSnackbar({ open: true, message: 'Track removed' });
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to remove track' });
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isLoading || !playlist) {
    return (
      <Dialog open onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Filter out tracks already in playlist
  const playlistTrackIds = new Set((playlist.tracks || []).map((item: any) => item.content_id));
  const filteredContent = availableContent.filter(
    (c: any) => !playlistTrackIds.has(c.id) &&
      (c.title?.toLowerCase().includes(searchTrack.toLowerCase()) ||
       c.artist?.toLowerCase().includes(searchTrack.toLowerCase()))
  );

  return (
    <>
      <Dialog open onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <QueueMusic sx={{ color: playlist.color }} />
            <Typography variant="h6" sx={{ flex: 1 }}>{playlist.name}</Typography>
            <Chip
              label={`${playlist.tracks?.length || 0} tracks`}
              size="small"
              icon={<MusicNote />}
            />
            <IconButton onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Add Track Section */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PlaylistAdd /> Add Tracks
            </Typography>
            <Autocomplete
              options={filteredContent}
              getOptionLabel={(option: any) => `${option.title} - ${option.artist}`}
              inputValue={searchTrack}
              onInputChange={(_, value) => setSearchTrack(value)}
              onChange={(_, value) => {
                if (value) handleAddTrack(value.id);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search for tracks to add..."
                  size="small"
                  fullWidth
                />
              )}
              renderOption={(props, option: any) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{option.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.artist} • {formatDuration(option.duration || 0)}
                    </Typography>
                  </Box>
                </li>
              )}
              noOptionsText={searchTrack ? "No matching tracks found" : "Type to search..."}
            />
          </Paper>

          {/* Track List */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Playlist Tracks ({playlist.tracks?.length || 0})
          </Typography>
          {playlist.tracks?.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <MusicNote sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">
                No tracks yet. Use the search above to add tracks.
              </Typography>
            </Paper>
          ) : (
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {playlist.tracks?.map((item: any, index: number) => (
                <ListItem
                  key={item.id}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <Typography variant="body2" sx={{ width: 30, color: 'text.secondary' }}>
                    {index + 1}
                  </Typography>
                  <ListItemText
                    primary={item.title || 'Unknown Title'}
                    secondary={`${item.artist || 'Unknown Artist'} • ${formatDuration(item.duration || 0)}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleRemoveTrack(item.id)}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </>
  );
};

export default PlaylistsPage;
